import { useRef, useCallback, useEffect } from 'react'
import type { Particle, ChainConfig } from '@/types'
import { distance } from '@/utils/vector'
import { initAudioOnInteraction, playPrimaryClick, playSecondaryClick } from '@/utils/sound'

interface ChainInteractionOptions {
    canvasRef: React.RefObject<HTMLCanvasElement | null>
    particlesRef: React.RefObject<Particle[]>
    config: ChainConfig
    onToggle: () => void
    setParticlePosition: (index: number, pos: { x: number; y: number }) => void
    applyImpulse: (index: number, impulse: { x: number; y: number }) => void
    getRestYPosition: () => number
}

export function useChainInteraction({
    canvasRef,
    particlesRef,
    config,
    onToggle,
    setParticlePosition,
    applyImpulse,
    getRestYPosition,
}: ChainInteractionOptions) {
    const isDraggingRef = useRef(false)
    const dragIndexRef = useRef<number | null>(null)
    const dragOffsetRef = useRef({ x: 0, y: 0 })
    const hasTriggeredRef = useRef(false)
    const pullProgressRef = useRef(0)

    const getCanvasCoords = useCallback(
        (e: PointerEvent | Touch) => {
            const canvas = canvasRef.current
            if (!canvas) return { x: 0, y: 0 }
            const rect = canvas.getBoundingClientRect()
            const dpr = window.devicePixelRatio || 1
            return {
                x: (e.clientX - rect.left) * dpr,
                y: (e.clientY - rect.top) * dpr,
            }
        },
        [canvasRef],
    )

    const hitTest = useCallback(
        (pos: { x: number; y: number }): number | null => {
            const particles = particlesRef.current
            // Prefer handle (last bead) — test it first with generous hitbox
            const handle = particles[particles.length - 1]
            if (handle && distance(pos, handle.position) < handle.radius * 3) {
                return particles.length - 1
            }
            // Then test other beads
            for (let i = particles.length - 2; i >= 1; i--) {
                const p = particles[i]
                if (distance(pos, p.position) < p.radius * 3) {
                    return i
                }
            }
            return null
        },
        [particlesRef],
    )

    const calculatePullProgress = useCallback(
        (handleY: number): number => {
            const restY = getRestYPosition()
            const displacement = handleY - restY
            if (displacement <= 0) return 0
            return Math.min(displacement / config.pullThreshold, 1.0)
        },
        [config.pullThreshold, getRestYPosition],
    )

    const calculateResistance = useCallback(
        (progress: number): number => {
            if (progress < 0.3) return 0
            const normalized = (progress - 0.3) / 0.7
            return Math.pow(normalized, config.pullResistanceCurve)
        },
        [config.pullResistanceCurve],
    )

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const onPointerDown = (e: PointerEvent) => {
            initAudioOnInteraction()

            const pos = getCanvasCoords(e)
            const hitIndex = hitTest(pos)

            if (hitIndex !== null) {
                e.preventDefault()
                isDraggingRef.current = true
                dragIndexRef.current = hitIndex
                hasTriggeredRef.current = false

                const p = particlesRef.current[hitIndex]
                p.pinned = true // Prevent verlet integrator from moving it
                dragOffsetRef.current = {
                    x: p.position.x - pos.x,
                    y: p.position.y - pos.y,
                }

                try {
                    canvas.setPointerCapture(e.pointerId)
                } catch {
                    // Synthetic events may not support pointer capture
                }
            }
        }

        const onPointerMove = (e: PointerEvent) => {
            if (!isDraggingRef.current || dragIndexRef.current === null) return
            e.preventDefault()

            const pos = getCanvasCoords(e)
            const targetX = pos.x + dragOffsetRef.current.x
            let targetY = pos.y + dragOffsetRef.current.y

            const dragIndex = dragIndexRef.current
            const handleIdx = particlesRef.current.length - 1

            // Calculate pull progress using the dragged bead's downward displacement.
            // Offset targetY so it's as if the handle were being dragged:
            // the handle sits (handleIdx - dragIndex) segments below this bead.
            const offsetY = targetY + (handleIdx - dragIndex) * config.segmentLength
            const progress = calculatePullProgress(offsetY)
            pullProgressRef.current = progress
            const resistance = calculateResistance(progress)

            // Apply resistance — lerp target toward this bead's rest position
            const beadRestY = getRestYPosition() - (handleIdx - dragIndex) * config.segmentLength
            targetY = targetY - (targetY - beadRestY) * resistance * 0.7

            setParticlePosition(dragIndex, { x: targetX, y: targetY })

            // Check threshold
            if (progress >= 1.0 && !hasTriggeredRef.current) {
                hasTriggeredRef.current = true
                onToggle()
                playPrimaryClick()

                // Unpin and release drag, apply upward snap impulse
                particlesRef.current[dragIndex].pinned = false
                isDraggingRef.current = false
                dragIndexRef.current = null
                applyImpulse(handleIdx, {
                    x: 0,
                    y: -8,
                })

                // Secondary click after settle
                setTimeout(() => {
                    playSecondaryClick()
                }, 150)

                return
            }
        }

        const onPointerUp = (e: PointerEvent) => {
            if (isDraggingRef.current && dragIndexRef.current !== null) {
                // Unpin the dragged particle so physics resumes
                const p = particlesRef.current[dragIndexRef.current]
                if (p && dragIndexRef.current !== 0) {
                    p.pinned = false
                }
                try {
                    canvas.releasePointerCapture(e.pointerId)
                } catch {
                    // Ignore
                }
            }
            isDraggingRef.current = false
            dragIndexRef.current = null
            pullProgressRef.current = 0
        }

        // On mobile, the browser decides to scroll before pointerdown fires.
        // We need a non-passive touchstart to preventDefault when hitting a bead,
        // otherwise the browser claims the gesture and cancels pointer events.
        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 1) return
            const pos = getCanvasCoords(e.touches[0])
            const hitIndex = hitTest(pos)
            if (hitIndex !== null) {
                e.preventDefault()
            }
        }

        canvas.addEventListener('touchstart', onTouchStart, { passive: false })
        canvas.addEventListener('pointerdown', onPointerDown)
        canvas.addEventListener('pointermove', onPointerMove)
        canvas.addEventListener('pointerup', onPointerUp)
        canvas.addEventListener('pointercancel', onPointerUp)

        return () => {
            canvas.removeEventListener('touchstart', onTouchStart)
            canvas.removeEventListener('pointerdown', onPointerDown)
            canvas.removeEventListener('pointermove', onPointerMove)
            canvas.removeEventListener('pointerup', onPointerUp)
            canvas.removeEventListener('pointercancel', onPointerUp)
        }
    }, [
        canvasRef,
        particlesRef,
        config,
        onToggle,
        setParticlePosition,
        applyImpulse,
        getCanvasCoords,
        hitTest,
        calculatePullProgress,
        calculateResistance,
        getRestYPosition,
    ])

    return {
        isDraggingRef,
        pullProgressRef,
    }
}
